import { existsSync, readFileSync } from "node:fs";
import { globalSettingsPath, mapLegacyFields } from "@skaile/workspaces/core";
import { type AuthDeps, getAccessToken, hasStoredAuth, loadStoreAuthConfig } from "./store-auth.ts";

/**
 * Connection configuration for the AI Asset Store API. `jwt` is normally left
 * null here and filled per-call by the caller from {@link getStoreToken} (the
 * device-flow session); it stays on the type for the `{ ...config, jwt }` spread.
 *
 * @docLink cli/dev-guide#store-client
 */
export interface StoreConfig {
  url: string;
  jwt: string | null;
}

/**
 * Read the store API base URL (settings `storeUrl` → `SKAILE_STORE_URL` →
 * default). Auth is not read here — it lives in the device-flow session; see
 * {@link getStoreToken}.
 *
 * @docLink cli/dev-guide#store-client
 */
export function getStoreConfig(): StoreConfig {
  const settingsPath = globalSettingsPath();
  let settings: Record<string, any> = {};
  if (existsSync(settingsPath)) {
    try {
      settings = mapLegacyFields(JSON.parse(readFileSync(settingsPath, "utf-8")));
    } catch {
      settings = {};
    }
  }
  return {
    url: settings.storeUrl ?? process.env.SKAILE_STORE_URL ?? "https://api.skaile.store",
    jwt: null,
  };
}

/**
 * Return a valid store access token (Keycloak device-flow session, refreshed if
 * needed). Throws a "run `skaile store login`" error when there is no session.
 * This is the auth source for every `/api/v1/*` publisher call.
 */
export function getStoreToken(deps?: AuthDeps): Promise<string> {
  return getAccessToken(loadStoreAuthConfig(), deps);
}

/**
 * Make an authenticated HTTP request to the store REST API.
 *
 * @param config - Store connection config (URL + auth token).
 * @param path - API path (e.g. `/api/assets`).
 * @param opts - Optional method, body, query params, extra headers, and
 *   `timeoutMs` (default 1500). Publisher calls pass a longer timeout because
 *   they trigger server-side clone/sync work.
 * @returns Parsed JSON response body.
 * @docLink cli/dev-guide#store-client
 */
export async function storeFetch<T = any>(
  config: StoreConfig,
  path: string,
  opts?: {
    method?: string;
    body?: unknown;
    params?: Record<string, string>;
    headers?: Record<string, string>;
    timeoutMs?: number;
  },
): Promise<T> {
  let url = `${config.url}${path}`;
  if (opts?.params) {
    const sp = new URLSearchParams(opts.params);
    url += `?${sp}`;
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(opts?.headers ?? {}),
  };
  if (config.jwt) {
    headers.Authorization = `Bearer ${config.jwt}`;
  }

  const fetchOpts: RequestInit = {
    method: opts?.method ?? "GET",
    headers,
  };
  if (opts?.body) {
    fetchOpts.body = JSON.stringify(opts.body);
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), opts?.timeoutMs ?? 1_500);
  try {
    const res = await fetch(url, { ...fetchOpts, signal: controller.signal });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      // Prefer the store's human-readable `message` (e.g. "Namespace '@x' is
      // reserved … Sign in with GitHub to claim it") over the bare `error` code
      // (`UNPROCESSABLE_CONTENT`) — the message is the actionable part.
      const b = body as { message?: string; error?: string };
      throw new Error(b.message ?? b.error ?? `Store API error: ${res.status}`);
    }
    return res.json() as Promise<T>;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Return `true` when a store session exists (a Keycloak device-flow token cached
 * at `~/.skaile/auth.json`). Validity/refresh is resolved on use via
 * {@link getStoreToken}.
 *
 * @docLink cli/dev-guide#store-client
 */
export function isStoreAuthenticated(): boolean {
  return hasStoredAuth(loadStoreAuthConfig());
}
